export type LocalRecordingSupport =
    | { supported: true; mimeType: string }
    | { supported: false; reason: 'browser' | 'capabilities' };

export type BrowserBrand = 'chrome' | 'safari' | 'firefox' | 'other';

export type BrowserEnvironment = {
    userAgent: string;
    brands?: readonly { brand: string }[];
    hasMediaRecorder: boolean;
    hasCanvasCapture: boolean;
    hasWebAudio: boolean;
    isTypeSupported: (mimeType: string) => boolean;
};

const MIME_TYPES = [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4;codecs=avc1,mp4a',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
];

const CHROMIUM_OWN_BRANDS: Record<string, true> = { 'Chromium': true, 'Google Chrome': true };
const FOREIGN_UA_MARKERS =
    /Edg(?:e|A|iOS)?\/|OPR\/|Opera|YaBrowser|Yowser|SamsungBrowser|UCBrowser|Vivaldi|Brave|DuckDuckGo|Whale\//;

export function detectBrowserBrand({ userAgent, brands }: Pick<BrowserEnvironment, 'userAgent' | 'brands'>): BrowserBrand {
    if (FOREIGN_UA_MARKERS.test(userAgent)) return 'other';
    if (brands && brands.length > 0) {
        const vendorBrands = brands.map(({ brand }) => brand).filter((brand) => !/not.?a.?brand/i.test(brand));
        return vendorBrands.length > 0 && vendorBrands.every((brand) => CHROMIUM_OWN_BRANDS[brand]) ? 'chrome' : 'other';
    }
    if (/Firefox\/|FxiOS\//.test(userAgent)) return 'firefox';
    if (/Chrome\/|Chromium\/|CriOS\//.test(userAgent)) return 'chrome';
    if (userAgent.includes('Safari/') && userAgent.includes('Version/')) return 'safari';
    return 'other';
}

export function pickMimeType(isTypeSupported: (mimeType: string) => boolean): string | undefined {
    return MIME_TYPES.find((mimeType) => isTypeSupported(mimeType));
}

export function detectLocalRecordingSupport(environment: BrowserEnvironment): LocalRecordingSupport {
    if (detectBrowserBrand(environment) === 'other') return { supported: false, reason: 'browser' };
    if (!environment.hasMediaRecorder || !environment.hasCanvasCapture || !environment.hasWebAudio) {
        return { supported: false, reason: 'capabilities' };
    }
    const mimeType = pickMimeType(environment.isTypeSupported);
    return mimeType ? { supported: true, mimeType } : { supported: false, reason: 'capabilities' };
}

type NavigatorWithUserAgentData = Navigator & { userAgentData?: { brands: { brand: string }[] } };

export function currentBrowserEnvironment(): BrowserEnvironment {
    const hasMediaRecorder = typeof MediaRecorder === 'function';
    return {
        userAgent: navigator.userAgent,
        brands: (navigator as NavigatorWithUserAgentData).userAgentData?.brands,
        hasMediaRecorder,
        hasCanvasCapture: typeof HTMLCanvasElement !== 'undefined' && 'captureStream' in HTMLCanvasElement.prototype,
        hasWebAudio: typeof AudioContext === 'function' && 'createMediaStreamDestination' in AudioContext.prototype,
        isTypeSupported: (mimeType) => hasMediaRecorder && MediaRecorder.isTypeSupported(mimeType),
    };
}

export function fileExtension(mimeType: string): 'mp4' | 'webm' {
    return mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';
}
